/**
 * Procedural SFX via WebAudio. Avoids shipping any audio files in the MVP.
 * Each call synthesizes a short envelope-shaped tone or noise burst.
 *
 * PWA / mobile note: we do NOT gate synth on AudioContext.state. iOS Safari
 * needs an oscillator to actually start inside the unlock gesture for the
 * context to truly transition to 'running' — dropping calls here breaks
 * the unlock. The visibility handler installed in unlockAudioOnFirstGesture
 * suspends the context while the page is hidden, which is enough to stop
 * SFX from queuing up at a frozen currentTime and firing as a chord burst
 * when the PWA returns to the foreground.
 */

let ctx: AudioContext | null = null;

function ac(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();
  }
  return ctx;
}

/** Shared accessor for other audio modules (Music). */
export function getAudioContext(): AudioContext { return ac(); }

/** Resume audio on first user gesture (autoplay policy), and install a
 *  visibilitychange handler that suspends the context while hidden so the
 *  Music scheduler stops accruing backlog and SFX don't queue mid-air. */
export function unlockAudioOnFirstGesture(): void {
  const resume = () => {
    void ac().resume();
    window.removeEventListener('pointerdown', resume);
    window.removeEventListener('keydown', resume);
    installVisibilityHandler();
  };
  window.addEventListener('pointerdown', resume, { once: true });
  window.addEventListener('keydown', resume, { once: true });
}

let visibilityInstalled = false;
function installVisibilityHandler(): void {
  if (visibilityInstalled) return;
  visibilityInstalled = true;
  // Suspend on hide so Music.scheduleAhead's `while (nextStepTime < horizon)`
  // loop can't pile up hundreds of scheduled steps when the OS pauses the PWA;
  // resume on show so the next user interaction doesn't have to.
  document.addEventListener('visibilitychange', () => {
    const a = ac();
    if (document.visibilityState === 'hidden') {
      void a.suspend();
    } else if (document.visibilityState === 'visible') {
      void a.resume();
    }
  });
}

function tone(freq: number, durMs: number, type: OscillatorType, gain: number): void {
  const a = ac();
  const t0 = a.currentTime;
  const dur = durMs / 1000;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + dur);
}

function noiseBurst(durMs: number, gain: number, hpFreq = 800): void {
  const a = ac();
  const t0 = a.currentTime;
  const dur = durMs / 1000;
  const buffer = a.createBuffer(1, Math.max(1, Math.floor(a.sampleRate * dur)), a.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = a.createBufferSource();
  src.buffer = buffer;
  const filt = a.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = hpFreq;
  const g = a.createGain();
  g.gain.value = gain;
  src.connect(filt).connect(g).connect(a.destination);
  src.start(t0);
}

/** Oscillator with an exponential frequency sweep — gives motion sense to
 *  impact / launch sounds and makes pitch relationships explicit. */
function sweep(
  freqStart: number, freqEnd: number,
  durMs: number, type: OscillatorType, gain: number,
): void {
  const a = ac();
  const t0 = a.currentTime;
  const dur = durMs / 1000;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(Math.max(1, freqStart), t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t0 + dur * 0.85);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + dur);
}

/** White noise through a bandpass filter that slides in frequency over time.
 *  Creates the Aeolian "whoosh" character of air moving past an obstacle —
 *  much more musical than a static highpass because the spectral centre
 *  actually moves, giving an impression of speed and pitch. */
function bandpassSweepNoise(
  durMs: number, gain: number,
  freqStart: number, freqEnd: number, Q = 3,
): void {
  const a = ac();
  const t0 = a.currentTime;
  const dur = durMs / 1000;
  const buf = a.createBuffer(1, Math.max(1, a.sampleRate * dur | 0), a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = a.createBufferSource();
  src.buffer = buf;
  const filt = a.createBiquadFilter();
  filt.type = 'bandpass';
  filt.frequency.setValueAtTime(Math.max(1, freqStart), t0);
  filt.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t0 + dur * 0.75);
  filt.Q.value = Q;
  const g = a.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filt).connect(g).connect(a.destination);
  src.start(t0);
}

let muted = false;

export function setSfxMuted(m: boolean): void { muted = m; }
export function isSfxMuted(): boolean { return muted; }

export const Sfx = {
  hookAttach(): void { if (muted) return; tone(720, 90, 'triangle', 0.18); },
  hookRelease(): void { if (muted) return; tone(420, 70, 'triangle', 0.14); },
  hookMiss(): void { if (muted) return; tone(180, 60, 'square', 0.08); },
  death(): void { if (muted) return; tone(180, 220, 'sawtooth', 0.18); noiseBurst(220, 0.08); },
  win(): void {
    if (muted) return;
    tone(660, 120, 'triangle', 0.18);
    setTimeout(() => tone(880, 140, 'triangle', 0.18), 120);
    setTimeout(() => tone(1320, 220, 'triangle', 0.18), 260);
  },
  /** Trampoline / bouncy-platform hit. Pitch optionally varies with power. */
  trampoline(power = 16): void {
    if (muted) return;
    const f = 280 + Math.min(power, 32) * 14;
    tone(f, 80, 'sine', 0.22);
    setTimeout(() => tone(f * 1.5, 70, 'sine', 0.14), 30);
  },
  /** Booster cannon kick.
   *  Three harmonically coherent layers:
   *  1. Sub-bass body: 808-style pitch fall 200→45 Hz (sine) — the "thump".
   *  2. Octave layer: 400→90 Hz (triangle) — exactly 2× = one octave above;
   *     tracks the fundamental throughout the sweep so the ratio stays clean.
   *  3. Launch arc: 280→840 Hz rising sine — 840/280 = 3×, a perfect 12th,
   *     giving a sense of forward propulsion without clashing the bass.
   *  Noise burst supplies the attack transient (click/crack on impact). */
  booster(): void {
    if (muted) return;
    sweep(200, 45, 160, 'sine', 0.28);                           // sub-bass pitch fall
    sweep(400, 90, 120, 'triangle', 0.10);                       // octave above (2×), tracks kick
    noiseBurst(55, 0.13, 500);                                   // attack crack
    setTimeout(() => sweep(280, 840, 110, 'sine', 0.07), 35);   // 3× rise = launch arc
  },
  /** Light platform tap (non-bouncy land). */
  thud(): void {
    if (muted) return;
    tone(90, 70, 'sine', 0.14);
    noiseBurst(50, 0.04, 200);
  },
  /** Whoosh while swinging through air at high speed.
   *  Bandpass noise with a rising frequency sweep mimics the Aeolian tone
   *  of air rushing past a rope — much more tonal than flat highpass noise.
   *  A subtle sine undertone anchors it to a pitch so it doesn't clash the
   *  music. Both centre frequency and duration grow with swing intensity. */
  swoosh(intensity = 1): void {
    if (muted) return;
    const clamped = Math.max(0, Math.min(intensity, 3));
    const g = Math.min(0.13, 0.03 + clamped * 0.035);
    const durMs = 150 + clamped * 40;
    // BPF sweeps from a warm low centre upward — rising pitch = acceleration
    bandpassSweepNoise(durMs, g * 0.9, 180 + clamped * 50, 480 + clamped * 180, 2.5);
    // Sine undertone for the "Aeolian hum" — only audible at moderate+ speed
    if (clamped > 0.8) tone(160 + clamped * 30, durMs * 0.65, 'sine', g * 0.3);
  },
  /** UI click. */
  uiClick(): void { if (muted) return; tone(880, 35, 'square', 0.10); },
  /** Switch activation: bright ascending two-note "klink". */
  switchOn(): void {
    if (muted) return;
    tone(660, 50, 'square', 0.14);
    setTimeout(() => tone(990, 90, 'triangle', 0.16), 35);
  },
  /** Switch deactivation: dull descending "klunk". */
  switchOff(): void {
    if (muted) return;
    tone(440, 60, 'square', 0.12);
    setTimeout(() => tone(260, 110, 'triangle', 0.12), 45);
  },
  /** Pause toggle. */
  pause(): void {
    if (muted) return;
    tone(520, 60, 'triangle', 0.12);
    setTimeout(() => tone(390, 80, 'triangle', 0.10), 50);
  },
  /** Level start fanfare. */
  start(): void {
    if (muted) return;
    tone(523, 90, 'triangle', 0.14); // C5
    setTimeout(() => tone(659, 90, 'triangle', 0.14), 90);  // E5
    setTimeout(() => tone(784, 140, 'triangle', 0.16), 180); // G5
  },
};
